import React, {
  useState, useEffect, useMemo, useRef, useCallback,
} from 'react';
import backendService from 'services/backend.service';
import { notifyOk, notifyError } from 'libs/utils/Toaster';
import { IReferralAgreementData } from './ReferralAgreementForm';
import EntModal, { IEntityConfig, IFamilyEntity, IInheritedFrom } from './EntModal';
import ApmModal from './ApmModal';

interface IApmEntityPageProps {
  agreement: IReferralAgreementData;
  onBack: () => void;
  onAgreementUpdated: (updated: IReferralAgreementData) => Promise<void>;
}

type TabType = 'MERCHANT' | 'PARENT' | 'GRANDPARENT';

const TAB_LABELS: Record<TabType, string> = {
  MERCHANT: 'Merchants',
  PARENT: 'Parents',
  GRANDPARENT: 'Grandparents',
};

function cfgBipsLabel(cfg: IEntityConfig): string {
  return cfg.tiered ? `Tiered (${cfg.tiers.length} tiers)` : `${cfg.flatBips} bps`;
}

function apmBipsLabel(agreement: IReferralAgreementData): string {
  return agreement.tiered
    ? `Tiered (${(agreement.tiers ?? []).length} tiers)`
    : `${agreement.bips ?? 0} bps`;
}

// Returns the effective inherited config+source following Merchant→Parent→GP→APM priority
function getInheritedFrom(
  entity: IFamilyEntity,
  configs: Record<string, IEntityConfig>,
  agreement: IReferralAgreementData,
): IInheritedFrom {
  if (entity.type === 'MERCHANT') {
    if (entity.parentId && configs[entity.parentId] && !configs[entity.parentId].excluded) {
      return {
        label: cfgBipsLabel(configs[entity.parentId]),
        source: `Parent override (${entity.parentName ?? 'Parent'})`,
      };
    }
    if (entity.grandparentId && configs[entity.grandparentId] && !configs[entity.grandparentId].excluded) {
      return {
        label: cfgBipsLabel(configs[entity.grandparentId]),
        source: `Grandparent override (${entity.grandparentName ?? 'Grandparent'})`,
      };
    }
  }
  if (entity.type === 'PARENT' && entity.grandparentId && configs[entity.grandparentId] && !configs[entity.grandparentId].excluded) {
    return {
      label: cfgBipsLabel(configs[entity.grandparentId]),
      source: `Grandparent override (${entity.grandparentName ?? 'Grandparent'})`,
    };
  }
  return {
    label: apmBipsLabel(agreement),
    source: `Agreement default (${agreement.targetName ?? 'agreement'})`,
  };
}

// Effective bips display for the table — traverses full hierarchy
function effectiveBipsLabel(
  entity: IFamilyEntity,
  configs: Record<string, IEntityConfig>,
  agreement: IReferralAgreementData,
): string {
  const own = configs[entity.id];
  if (own) {
    if (own.excluded) return '— excluded';
    return cfgBipsLabel(own);
  }
  // Cascade: show excluded if any ancestor is excluded
  if (hasAncestorExclusion(entity, configs)) return '— excluded';
  const inherited = getInheritedFrom(entity, configs, agreement);
  return `${inherited.label} ↑`;
}

function hasAncestorExclusion(
  entity: IFamilyEntity,
  configs: Record<string, IEntityConfig>,
): string | null {
  if (entity.type === 'MERCHANT' && entity.parentId) {
    const parentCfg = configs[entity.parentId];
    if (parentCfg?.excluded) return `Parent "${entity.parentName}" is excluded`;
    if (entity.grandparentId) {
      const gpCfg = configs[entity.grandparentId];
      if (gpCfg?.excluded) return `Grandparent "${entity.grandparentName}" is excluded`;
    }
  }
  if (entity.type === 'PARENT' && entity.grandparentId) {
    const gpCfg = configs[entity.grandparentId];
    if (gpCfg?.excluded) return `Grandparent "${entity.grandparentName}" is excluded`;
  }
  return null;
}

const BRAND_COLORS = [
  '#1a1a2e', '#c8102e', '#006341', '#e94560',
  '#0f3460', '#533483', '#00704a', '#4a1942',
  '#2c3e50', '#16a085', '#8e44ad', '#1e40af',
];

function brandColor(name: string): string {
  if (!name) return '#6B7280';
  let idx = 0;
  for (let i = 0; i < name.length; i += 1) {
    idx = (idx + name.charCodeAt(i)) % BRAND_COLORS.length;
  }
  return BRAND_COLORS[idx];
}

function brandInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  GRANDPARENT: 'Grandparent',
  PARENT: 'Parent',
  MERCHANT: 'Merchant',
};

function EntityTypeBadge({ type }: { type: string }) {
  return (
    <span className={`et-badge et-badge--${type.toLowerCase()}`}>
      {ENTITY_TYPE_LABELS[type] ?? type}
    </span>
  );
}

const ApmEntityPage: React.FC<IApmEntityPageProps> = ({
  agreement,
  onBack,
  onAgreementUpdated,
}) => {
  const [entities, setEntities] = useState<IFamilyEntity[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [configs, setConfigs] = useState<Record<string, IEntityConfig>>({});
  const [configsLoading, setConfigsLoading] = useState(true);
  const [tab, setTab] = useState<TabType>('MERCHANT');
  const [q, setQ] = useState('');
  const [ovQ, setOvQ] = useState('');
  const [showDrop, setShowDrop] = useState(false);
  const [selEnt, setSelEnt] = useState<IFamilyEntity | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const brandName = agreement.targetName ?? (agreement.targetType === 'CONNECTOR' ? 'Connector' : 'Brand');

  // Fetch all entities
  useEffect(() => {
    const load = async () => {
      setEntitiesLoading(true);
      try {
        const [merchants, parents, grandparents] = await Promise.all([
          backendService.getMerchants().catch(() => []),
          backendService.getParents().catch(() => []),
          backendService.getGrandparents().catch(() => []),
        ]);

        // gpMap keyed by grandparent UUID
        const gpMap: Record<string, { id: string; name: string }> = {};
        (grandparents as any[]).forEach((gp) => {
          gpMap[gp.id] = { id: gp.id, name: gp.name };
        });

        // parMap keyed by parent UUID; parentId field from API = grandparent UUID
        const parMap: Record<string, { id: string; name: string; grandparentId?: string }> = {};
        (parents as any[]).forEach((p) => {
          parMap[p.id] = { id: p.id, name: p.name, grandparentId: p.parentId ?? undefined };
        });

        const gpList: IFamilyEntity[] = (grandparents as any[]).map((gp) => ({
          id: gp.id,
          name: gp.name,
          type: 'GRANDPARENT' as const,
        }));

        const parList: IFamilyEntity[] = (parents as any[]).map((p) => {
          const gp = p.parentId ? gpMap[p.parentId] : undefined;
          return {
            id: p.id,
            name: p.name,
            type: 'PARENT' as const,
            grandparentId: p.parentId ?? undefined,
            grandparentName: gp?.name,
          };
        });

        const merList: IFamilyEntity[] = (merchants as any[]).map((m) => {
          // m.parentId = parent UUID (from updated API)
          const par = m.parentId ? parMap[m.parentId] : undefined;
          const gpId = par?.grandparentId;
          const gp = gpId ? gpMap[gpId] : undefined;
          return {
            id: m.id,
            name: m.name,
            type: 'MERCHANT' as const,
            parentId: m.parentId ?? undefined,
            parentName: par?.name,
            grandparentId: gpId,
            grandparentName: gp?.name,
          };
        });

        setEntities([...merList, ...parList, ...gpList]);
      } catch {
        notifyError('Failed to load entities');
      } finally {
        setEntitiesLoading(false);
      }
    };
    load();
  }, []);

  // Fetch entity configs for this agreement
  const loadConfigs = useCallback(async () => {
    if (!agreement.id) return;
    setConfigsLoading(true);
    try {
      const cfgs = await backendService.getReferralEntityConfigs(agreement.id);
      const map: Record<string, IEntityConfig> = {};
      cfgs.forEach((c: any) => {
        map[c.familyEntityId] = {
          id: c.id,
          familyEntityId: c.familyEntityId,
          excluded: c.excluded,
          tiered: c.tiered,
          flatBips: c.flatBips,
          tiers: c.tiers ?? [],
          exclusionReason: c.exclusionReason ?? null,
        };
      });
      setConfigs(map);
    } catch {
      notifyError('Failed to load entity configs');
    } finally {
      setConfigsLoading(false);
    }
  }, [agreement.id]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  // Close dropdown on outside click
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDrop(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const searchHits = useMemo(() => {
    if (!q) return [];
    return entities.filter((e) => e.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8);
  }, [q, entities]);

  const tabEntities = useMemo(
    () => entities.filter((e) => e.type === tab),
    [entities, tab],
  );

  const filteredTabEntities = useMemo(() => {
    if (!ovQ) return tabEntities;
    return tabEntities.filter((e) => e.name.toLowerCase().includes(ovQ.toLowerCase()));
  }, [tabEntities, ovQ]);

  const handleSaveEntityConfig = async (cfg: IEntityConfig) => {
    if (!agreement.id) return;
    try {
      await backendService.upsertReferralEntityConfig(agreement.id, {
        familyEntityId: cfg.familyEntityId,
        excluded: cfg.excluded,
        tiered: cfg.tiered,
        flatBips: cfg.flatBips,
        tiers: cfg.tiers,
        exclusionReason: cfg.excluded ? (cfg.exclusionReason ?? null) : null,
      });
      notifyOk('Entity config saved');
      await loadConfigs();
    } catch {
      notifyError('Failed to save entity config');
      throw new Error('save failed');
    }
  };

  const periodLabel = agreement.period === 'CUSTOM'
    ? `${agreement.customDays ?? '?'}d`
    : ((agreement.period ?? 'MONTHLY').charAt(0) + (agreement.period ?? 'MONTHLY').slice(1).toLowerCase());

  const defaultBips = agreement.tiered
    ? `Tiered (${(agreement.tiers ?? []).length} tiers)`
    : `${agreement.bips ?? 0} bps default`;

  return (
    <div className="apm-entity-page">
      {/* Header */}
      <div className="apm-entity-header">
        <button
          className="btn btn-ghost apm-back-btn"
          onClick={onBack}
          type="button"
        >
          ← Back to Referral Agreements
        </button>
        <div className="apm-entity-header-main">
          <div className="apm-entity-header-left">
            <span
              className="apm-brand-badge apm-brand-badge--lg"
              style={{ background: brandColor(brandName) }}
            >
              {brandInitials(brandName)}
            </span>
            <div>
              <div className="apm-entity-title">
                {brandName}
                {' '}
                — Entity Configuration
              </div>
              <div className="apm-entity-meta">
                <span className={`ref-status-badge${agreement.isActive ? ' ref-status-badge--active' : ' ref-status-badge--inactive'}`}>
                  {agreement.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="apm-entity-meta-dot">·</span>
                <span>{defaultBips}</span>
                <span className="apm-entity-meta-dot">·</span>
                <span>{periodLabel}</span>
                <span className="apm-entity-meta-dot">·</span>
                <span>{agreement.invoicingCurrency}</span>
              </div>
            </div>
          </div>
          <button
            className="btn btn-outline"
            onClick={() => setSettingsOpen(true)}
            type="button"
          >
            ⚙ APM Settings
          </button>
        </div>
      </div>

      <div className="apm-entity-body">
        {/* Search card */}
        <div className="invoicing-form-child apm-search-card">
          <div className="apm-search-title">Search &amp; Configure Entity</div>
          <div className="apm-search-sub">
            Search for any merchant, parent, or grandparent to configure their
            {' '}
            {brandName}
            {' '}
            referral settings.
          </div>
          <div
            className="apm-search-wrap"
            ref={searchRef}
          >
            <div className="apm-search-inner">
              <span className="apm-search-icon">🔍</span>
              <input
                aria-label="Search entities"
                className="apm-search-input"
                onChange={(e) => { setQ(e.target.value); setShowDrop(true); }}
                onFocus={() => q && setShowDrop(true)}
                placeholder={`Search merchants, parents, grandparents for ${brandName}…`}
                value={q}
              />
            </div>
            {showDrop && searchHits.length > 0 && (
              <div
                className="apm-search-drop"
                role="listbox"
              >
                {searchHits.map((e) => (
                  <div
                    aria-selected={false}
                    className="apm-search-item"
                    key={e.id}
                    onClick={() => { setSelEnt(e); setQ(''); setShowDrop(false); }}
                    onKeyDown={(ev) => { if (ev.key === 'Enter') { setSelEnt(e); setQ(''); setShowDrop(false); } }}
                    role="option"
                    tabIndex={0}
                  >
                    <span>{e.name}</span>
                    <EntityTypeBadge type={e.type} />
                  </div>
                ))}
              </div>
            )}
            {showDrop && q && searchHits.length === 0 && (
              <div className="apm-search-drop">
                <div className="apm-search-item apm-search-item--empty">No entities found</div>
              </div>
            )}
          </div>
        </div>

        {/* Overview card */}
        <div className="invoicing-form-child">
          <div className="apm-overview-header">
            <div className="apm-overview-title">Configuration Overview</div>
            <input
              aria-label="Filter entities"
              className="apm-overview-filter"
              onChange={(e) => setOvQ(e.target.value)}
              placeholder="Filter entities…"
              value={ovQ}
            />
          </div>

          <div className="apm-tabs">
            {(['MERCHANT', 'PARENT', 'GRANDPARENT'] as TabType[]).map((t) => {
              const count = entities.filter((e) => e.type === t).length;
              return (
                <button
                  className={`apm-tab${tab === t ? ' apm-tab--active' : ''}`}
                  key={t}
                  onClick={() => setTab(t)}
                  type="button"
                >
                  {TAB_LABELS[t]}
                  {' '}
                  (
                  {count}
                  )
                </button>
              );
            })}
          </div>

          {(entitiesLoading || configsLoading) ? (
            <div className="apm-overview-table-wrap">
              <table className="apm-overview-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    {tab === 'MERCHANT' && <th>Parent</th>}
                    {tab !== 'GRANDPARENT' && <th>Grandparent</th>}
                    <th>BiPs</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {['a', 'b', 'c', 'd', 'e'].map((id) => (
                    <tr key={`skel-${id}`}>
                      <td className="apm-skel-td">
                        <span
                          className="skel"
                          style={{ height: 14, width: 160 }}
                        />
                      </td>
                      {tab === 'MERCHANT' && (
                        <td className="apm-skel-td">
                          <span
                            className="skel"
                            style={{ height: 14, width: 110 }}
                          />
                        </td>
                      )}
                      {tab !== 'GRANDPARENT' && (
                        <td className="apm-skel-td">
                          <span
                            className="skel"
                            style={{ height: 14, width: 120 }}
                          />
                        </td>
                      )}
                      <td className="apm-skel-td">
                        <span
                          className="skel skel--pill"
                          style={{ height: 22, width: 80 }}
                        />
                      </td>
                      <td className="apm-skel-td">
                        <span
                          className="skel skel--pill"
                          style={{ height: 22, width: 80 }}
                        />
                      </td>
                      <td className="apm-skel-td-right">
                        <span
                          className="skel"
                          style={{ borderRadius: 8, height: 30, width: 90 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="apm-overview-table-wrap">
              <table className="apm-overview-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    {tab === 'MERCHANT' && <th>Parent</th>}
                    {tab !== 'GRANDPARENT' && <th>Grandparent</th>}
                    <th>BiPs</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTabEntities.map((e) => {
                    const cfg = configs[e.id] ?? null;
                    const anc = hasAncestorExclusion(e, configs);
                    const isExcl = cfg?.excluded || !!anc;
                    let exclClass = 'excl-badge--included';
                    if (isExcl) {
                      exclClass = anc ? 'excl-badge--inherited' : 'excl-badge--excluded';
                    }
                    let exclLabel = 'Included';
                    if (isExcl) {
                      exclLabel = anc ? 'Excl. (inherited)' : 'Excluded';
                    }
                    return (
                      <tr key={e.id}>
                        <td className="apm-ov-name">{e.name}</td>
                        {tab === 'MERCHANT' && <td className="apm-ov-sub">{e.parentName ?? '—'}</td>}
                        {tab !== 'GRANDPARENT' && <td className="apm-ov-sub">{e.grandparentName ?? '—'}</td>}
                        <td>
                          <span className={`bips-chip${configs[e.id] ? ' bips-chip--custom' : ''}`}>
                            {effectiveBipsLabel(e, configs, agreement)}
                          </span>
                        </td>
                        <td>
                          <span className={`excl-badge ${exclClass}`}>
                            {exclLabel}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => setSelEnt(e)}
                            type="button"
                          >
                            Configure
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredTabEntities.length === 0 && (
                    <tr>
                      <td
                        className="apm-ov-empty"
                        colSpan={6}
                      >
                        No entities found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selEnt && (
        <EntModal
          agreement={agreement}
          ancestorExclusion={hasAncestorExclusion(selEnt, configs)}
          entity={selEnt}
          existingConfig={configs[selEnt.id] ?? null}
          inheritedFrom={getInheritedFrom(selEnt, configs, agreement)}
          onClose={() => setSelEnt(null)}
          onSave={handleSaveEntityConfig}
        />
      )}

      {settingsOpen && (
        <ApmModal
          agreement={agreement}
          brands={[]}
          connectors={[]}
          onClose={() => setSettingsOpen(false)}
          onSave={onAgreementUpdated}
        />
      )}
    </div>
  );
};

export default ApmEntityPage;
